import React from 'react';
import styled from 'styled-components';
import Button from 'components/button';
import Slider from './slider';
import { navigateToLeaseApplication } from '../../utils/navigate';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 320px;
  background-color: #fff;
  border: 1px solid #ddd;
  border-radius: 12px;
  line-height: 1.4;
  overflow: hidden;
  position: relative;
  cursor: pointer;
  &:hover {
    box-shadow: 0 3px 12px rgba(0, 0, 0, 0.2);
  }
  &.itemActive {
    outline: 1px solid var(--color-primary);
  }
`;

const SliderWrapper = styled.div`
  position: relative;
  flex: 1;
  overflow: hidden;
`;

const Footer = styled.div`
  display: flex;
  flex-flow: column;
  min-height: 75px;
  justify-content: center;
  padding: 15px 10px;
  flex-shrink: 0;
  font-size: 14px;
  .title {
    font-weight: bold;
    font-size: 16px;
    span {
      color: #888;
      font-weight: normal;
      font-size: 14px;
    }
  }
  .amenities {
    font-size: 14px;
  }
  .address {
    color: #777;
    font-size: 14px;
  }
`;

const Apply = styled(Button)`
  position: absolute;
  top: 15px;
  right: 10px;
  padding: 0 10px;
  font-weight: bold;
  color: #fff;
  background-color: #00a1ff;
  box-shadow: 0 1px 8px rgba(0, 0, 0, 0.2);
  font-weight: 500;
  border-radius: 25px;
  font-size: 13px;
  height: 24px;
`;

const MediaBadges = styled.div`
  display: flex;
  align-items: center;
  position: absolute;
  top: 15px;
  left: 10px;
  gap: 5px;
`;

const MediaBadge = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  height: 24px;
  padding: 0 8px;
  border-radius: 12px;
  background-color: rgba(255, 255, 255, 0.9);
  font-size: 13px;
`;

const Address = styled.div`
  color: #fff;
  position: absolute;
  bottom: 0px;
  padding: 5px 10px;
  text-shadow: 0 1px 2px #000;
  width: 100%;
  background: linear-gradient(0deg, rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0));
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  &.space-between {
    justify-content: space-between;
  }
  &.gap-10 {
    gap: 10px;
  }
  &.gap-5 {
    gap: 5px;
  }
  &.actions {
    margin-top: 5px;
  }
`;

const Badge = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  &.truncate {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    display: block;
  }
`;

const Action = styled(Button)`
  font-weight: bold;
  color: #fff;
  background-color: #00a1ff;
  font-weight: 500;
  border-radius: 4px;
  font-size: 14px;
  height: 28px;
  width: 60px;
  &.details {
    color: initial;
    font-weight: normal;
    background-color: #fff;
    &:hover {
      background-color: #f5f5f5;
    }
  }
`;

export default ({ location = {}, onClick, isActive }) => {
  const { displayInfo = {} } = location;

  const handleApply = (event) => {
    // This goes to app create, it will bounce and redirect as needed
    event.preventDefault();
    event.stopPropagation();
    navigateToLeaseApplication({ location });
  };

  const detailItems = React.useMemo(() => {
    const firstRowItems = [];
    if (displayInfo.bedCount) {
      firstRowItems.push(
        <>
          <strong>{displayInfo.bedCount}</strong> beds
        </>
      );
    }
    if (displayInfo.bathCount) {
      firstRowItems.push(
        <>
          <strong>{displayInfo.bathCount}</strong> baths
        </>
      );
    }
    if (displayInfo.squareFeet) {
      firstRowItems.push(
        <>
          <strong>{displayInfo.squareFeet}</strong> sf
        </>
      );
    }

    return firstRowItems;
  }, [location]);

  const { photosCount, videoCount } = React.useMemo(() => {
    return (displayInfo?.media || []).reduce(
      (acc, m) => {
        if (m.type === 'image' && !m.isComingSoon) acc.photosCount += 1;
        if (m.type === 'video') acc.videoCount += 1;
        return acc;
      },
      { photosCount: 0, videoCount: 0 }
    );
  }, [displayInfo?.media]);

  const handleShowing = () => {
    if (['custom', 'rently', 'showmojo'].includes(location.aptlyShowings?.type)) {
      // This goes to app create, it will bounce and redirect as needed
      window.open(location.aptlyShowings.link, '_blank');
    } else {
      // This goes to app create, it will bounce and redirect as needed
      window.open(
        `/${location.companyInfo.id}/showing/app/showing/create/?cId=${location._id}`,
        '_blank'
      );
    }
  };

  const showingEnabled = !!location?.aptlyShowings?.active;

  return (
    <>
      <Wrapper
        onClick={() => onClick(location)}
        className={`${isActive ? 'itemActive' : ''} propertyItem`}
        id={location._id}>
        <SliderWrapper>
          <Slider photos={displayInfo.photos} />
          <Address>{displayInfo.title}</Address>
        </SliderWrapper>
        <Footer>
          <Row className="space-between">
            {!!displayInfo.formattedPrice && (
              <div className="title">
                {displayInfo.formattedPrice}
                <span>/mo</span>
              </div>
            )}
            <div className="available">
              Available:{' '}
              <strong>
                {!displayInfo.formattedAvailableDate ? 'NOW' : displayInfo.formattedAvailableDate}
              </strong>
            </div>
          </Row>
          {location.address?.formattedAddress && (
            <Row>
              <Badge className="truncate">
                <i className="fa-solid fa-location-dot" /> {location.address?.formattedAddress}
              </Badge>
            </Row>
          )}
          <Row className="gap-10">
            {displayInfo.bedCount && (
              <Badge>
                <i className="fa-regular fa-bed" /> {displayInfo.bedCount}
              </Badge>
            )}
            {displayInfo.bathCount && (
              <Badge>
                <i className="fa-regular fa-bath" /> {displayInfo.bathCount}
              </Badge>
            )}
            {displayInfo.squareFeet && (
              <Badge>
                <i className="fa-regular fa-ruler-combined" /> {displayInfo.squareFeet} sf
              </Badge>
            )}
          </Row>

          {/* {!!location?.distanceFromCenter && (
            <div className="address">
              {location?.distanceFromCenter?.toLocaleString('fullwide', {
                maximumFractionDigits: 2
              })}{' '}
              miles away
            </div>
          )} */}

          <Row className="space-between actions">
            <Row className="gap-5">
              {location.aptlyScreening?.active && <Action onClick={handleApply}>Apply</Action>}
              {showingEnabled && <Action onClick={handleShowing}>Tour</Action>}
            </Row>
            <Row>
              <Action className="details">Details</Action>
            </Row>
          </Row>
        </Footer>
        {location.showPromoBanner && <Apply>{location.promoPreview || 'Coming Soon'}</Apply>}
        <MediaBadges>
          {photosCount > 0 && (
            <MediaBadge>
              <i className="fa-solid fa-camera" />
              {photosCount}
            </MediaBadge>
          )}
          {videoCount > 0 && (
            <MediaBadge>
              <i className="fa-solid fa-video" />
              {videoCount}
            </MediaBadge>
          )}
        </MediaBadges>
      </Wrapper>
    </>
  );
};
